import * as React from 'react';

import { Button } from '@ao-internal/components-react';

import NoImage from '../../assets/images/no-image.png';
import { ImageLoaderConfig } from '../../helpers/imageLoader';
import useImageLoader from '../../hooks/useImageLoader';
import { ChatMessageTypes, ChatMessageTypesClassMap } from '../../store';

export interface ExternalTrackingInfo {
	displayName: string;
	logoUrl: string;
	trackingUrl: string;
}

export interface DeliveryAddressModel {
	addressLine1: string;
	addressLine2: string;
	addressLine3: string;
	addressLine4: string;
	postcode: string;
}

export interface TrackedDeliveryMessageContent {
	imageSrc: string;
	imageAlt: string;
	orderDesc: string;
	plusMoreDesc?: string;
	deliveryDate: string;
	timeSlot?: string;
	address?: DeliveryAddressModel;
	externalTrackingInfo?: ExternalTrackingInfo;
}

interface TrackedDeliveryMessageProps {
	deliveryData: TrackedDeliveryMessageContent;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.TrackedDelivery]
}-message`;

export function buildAddressStr(address: DeliveryAddressModel): string {
	let str = '';
	if (address.addressLine1) {
		str = `${address.addressLine1}`;
	}
	if (address.addressLine3) {
		str = `${str}, ${address.addressLine3}`;
	}
	if (address.postcode) {
		str = `${str} ${address.postcode}`;
	}
	return str;
}

export const TrackedDeliveryMessage: React.FC<TrackedDeliveryMessageProps> = ({
	deliveryData,
}) => {
	const {
		imageSrc,
		imageAlt,
		orderDesc,
		plusMoreDesc,
		deliveryDate,
		timeSlot,
		address,
		externalTrackingInfo,
	} = deliveryData;

	const imagesToLoad: ImageLoaderConfig[] = [
		{ src: imageSrc, placeholder: NoImage },
	];
	if (externalTrackingInfo && externalTrackingInfo.logoUrl) {
		imagesToLoad.push({ src: externalTrackingInfo.logoUrl });
	}

	const [productImage, courierImage] = useImageLoader(imagesToLoad);

	return (
		<div
			data-testid={dataTestId}
			data-tag-area="tracked delivery message"
			className="mx-2 mb-2"
		>
			<div className="border-gray-40 w-full bg-white border border-solid rounded-md">
				<div className="rounded-t-md p-2 px-3 bg-white">
					<span className="text-display-sm">Your order</span>
				</div>
				<div className="flex items-center self-start p-3 pb-1 mb-0 break-words">
					<div>
						<img
							className="chat-tracked-product__image"
							src={productImage.src}
							alt={imageAlt}
						/>
					</div>
					<div className="flex-grow pl-4 pr-6">
						<strong className="chat-tracked-product__title text-title line-clamp-2">
							{orderDesc}
						</strong>
						{plusMoreDesc && (
							<React.Fragment>
								<p className="text-body-xs">{plusMoreDesc}</p>
							</React.Fragment>
						)}
					</div>
				</div>

				<hr className="border-gray-40 h-0 m-0 border-0 border-b border-solid" />

				<div className="p-3">
					<div className="text-small flex items-start pb-3">
						<div className="flex items-center pr-2">
							&#8203;
							<span
								className="ico ico-clock text-gray-70 block"
								role="img"
							></span>
						</div>
						<span className="text-body-sm flex-grow break-words">
							<span>{deliveryDate} </span>
							{timeSlot && (
								<React.Fragment>
									| <span>Between {timeSlot}</span>
								</React.Fragment>
							)}
						</span>
					</div>

					<div className="text-small flex items-start">
						<div className="flex items-center pr-2">
							&#8203;
							<span
								className="ico ico-location text-gray-70 block"
								role="img"
							></span>
						</div>
						<div>
							<span className="text-body-sm flex-grow break-words">
								{address && buildAddressStr(address)}
							</span>
							{externalTrackingInfo && (
								<div data-testid="courier-info" className="text-caption">
									{courierImage && courierImage.status === 'success' ? (
										<img
											data-testid="courier-logo"
											className="block mt-2"
											style={{ maxHeight: '32px' }}
											src={externalTrackingInfo.logoUrl}
											alt={externalTrackingInfo.displayName}
										/>
									) : (
										<span data-testid="courier-name" className="text-body-xs">
											Delivery partner: {externalTrackingInfo.displayName}
										</span>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{externalTrackingInfo && (
				<Button
					data-testid="track-delivery-cta"
					data-tag-name="track delivery link"
					data-tag-partner={externalTrackingInfo.displayName}
					as="a"
					variant="secondary"
					className="hover:bg-cta-actionhover hover:text-white block mt-2 text-center bg-white"
					href={externalTrackingInfo.trackingUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					Track my order through {externalTrackingInfo.displayName}
				</Button>
			)}
		</div>
	);
};

export default TrackedDeliveryMessage;
